import {
  createFileRoute,
  Link,
  Outlet,
  useCanGoBack,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import { apiJson, ApiError } from '@/lib/api';
import { track } from '@/lib/analytics';
import type { AdminUserDetailResponse, AdminUserNameUpdateResponse } from '@/lib/types';

function formatDisplayName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  fallback: string,
): string {
  return [firstName, lastName].filter(Boolean).join(' ').trim() || fallback;
}

function parseDetail(err: unknown): string {
  if (err instanceof ApiError && err.bodyText) {
    try {
      const j = JSON.parse(err.bodyText) as { detail?: string };
      if (j.detail) return j.detail;
    } catch {
      /* ignore */
    }
    return err.bodyText;
  }
  return err instanceof Error ? err.message : 'Request failed';
}

function EditNameDialog({
  open,
  firstName,
  lastName,
  pending,
  error,
  onClose,
  onConfirm,
}: {
  open: boolean;
  firstName: string | null;
  lastName: string | null;
  pending?: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (firstName: string, lastName: string) => void;
}) {
  const [draftFirst, setDraftFirst] = useState(firstName ?? '');
  const [draftLast, setDraftLast] = useState(lastName ?? '');
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraftFirst(firstName ?? '');
    setDraftLast(lastName ?? '');
    setConfirmed(false);
  }, [open, firstName, lastName]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !pending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, pending, onClose]);

  if (!open) return null;

  const currentLabel = formatDisplayName(firstName, lastName, '(no name)');
  const nextLabel = formatDisplayName(draftFirst.trim(), draftLast.trim(), '(no name)');
  const changed =
    draftFirst.trim() !== (firstName ?? '').trim() ||
    draftLast.trim() !== (lastName ?? '').trim();
  const canSave = changed && confirmed && !pending;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
      <div
        className='absolute inset-0 bg-black/40'
        aria-hidden
        onClick={() => {
          if (!pending) onClose();
        }}
      />
      <div
        className='relative z-10 bg-nova-card rounded-lg shadow-xl max-w-md w-full p-4 space-y-4 border border-nova-border'
        role='dialog'
        aria-modal='true'
        aria-labelledby='edit-name-dialog-title'
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id='edit-name-dialog-title' className='text-lg font-semibold text-nova-foreground'>
          Edit name
        </h3>
        <div className='space-y-3'>
          <label className='block'>
            <span className='block text-xs font-semibold text-nova-muted-foreground mb-1'>First name</span>
            <input
              value={draftFirst}
              onChange={(e) => setDraftFirst(e.target.value)}
              autoComplete='given-name'
              maxLength={100}
              className='w-full px-3 py-2 border border-nova-input rounded-md text-sm'
            />
          </label>
          <label className='block'>
            <span className='block text-xs font-semibold text-nova-muted-foreground mb-1'>Last name</span>
            <input
              value={draftLast}
              onChange={(e) => setDraftLast(e.target.value)}
              autoComplete='family-name'
              maxLength={100}
              className='w-full px-3 py-2 border border-nova-input rounded-md text-sm'
            />
          </label>
        </div>
        <p className='text-sm text-nova-muted-foreground'>
          This will change the name from <strong>{currentLabel}</strong> to{' '}
          <strong>{nextLabel}</strong>.
        </p>
        <label className='flex items-start gap-2 text-sm text-nova-foreground'>
          <input
            type='checkbox'
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className='mt-0.5'
          />
          I confirm this name change
        </label>
        {error ? <p className='text-sm text-red-700'>{error}</p> : null}
        <div className='flex justify-end gap-2 pt-2'>
          <button
            type='button'
            disabled={pending}
            onClick={onClose}
            className='px-4 py-2 text-sm font-semibold border border-nova-input rounded-md text-nova-foreground hover:bg-nova-accent disabled:opacity-50'
          >
            Cancel
          </button>
          <button
            type='button'
            disabled={!canSave}
            onClick={() => onConfirm(draftFirst.trim(), draftLast.trim())}
            className='px-4 py-2 text-sm font-semibold rounded-md bg-nova-primary text-white disabled:opacity-50'
          >
            {pending ? 'Saving…' : 'Save name'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminUserDetailLayout() {
  const { userId } = Route.useParams();
  const id = Number(userId);
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canGoBack = useCanGoBack();
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [editNameError, setEditNameError] = useState<string | null>(null);
  const [nameBanner, setNameBanner] = useState<{ ok: boolean; text: string } | null>(null);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const searchStr = useRouterState({ select: (s) => s.location.search });
  const hid = useMemo(() => new URLSearchParams(searchStr).get('hid') ?? undefined, [searchStr]);

  const userQuery = useQuery({
    queryKey: ['admin', 'user-detail', id],
    queryFn: () => apiJson<AdminUserDetailResponse>(getToken, `/v1/admin/users/${id}`),
  });

  const data = userQuery.data;

  const nameMutation = useMutation({
    mutationFn: (payload: { first_name: string; last_name: string }) =>
      apiJson<AdminUserNameUpdateResponse>(getToken, `/v1/admin/users/${id}/name`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
    onSuccess: async (result) => {
      setEditNameOpen(false);
      setEditNameError(null);
      setNameBanner({
        ok: true,
        text: `Name updated to ${formatDisplayName(result.first_name, result.last_name, '(no name)')}.`,
      });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'user-detail', id] });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'audit', id] });
    },
    onError: (e) => setEditNameError(parseDetail(e)),
  });

  const householdIds = useMemo(
    () => new Set((data?.households ?? []).map((h) => h.household_id)),
    [data?.households],
  );

  const base = `/admin/users/${userId}`;
  const pathTail = pathname.startsWith(base) ? pathname.slice(base.length) || '/' : '/';

  useEffect(() => {
    if (!data?.households?.length) return;
    const n = hid ? Number(hid) : NaN;
    if (hid && Number.isFinite(n) && householdIds.has(n)) return;

    const userBase = `/admin/users/${userId}`;
    const tail = pathTail === '/' ? '/' : pathTail;
    const payload =
      tail === '/' || tail === ''
        ? {
            to: '/admin/users/$userId' as const,
            params: { userId },
            search: { hid: String(data.households[0].household_id) },
            replace: true as const,
          }
        : tail === '/households'
          ? {
              to: '/admin/users/$userId/households' as const,
              params: { userId },
              search: { hid: String(data.households[0].household_id) },
              replace: true as const,
            }
          : tail === '/expenses'
            ? {
                to: '/admin/users/$userId/expenses' as const,
                params: { userId },
                search: { hid: String(data.households[0].household_id) },
                replace: true as const,
              }
            : tail === '/graphs'
              ? {
                  to: '/admin/users/$userId/graphs' as const,
                  params: { userId },
                  search: {
                    hid: String(data.households[0].household_id),
                    period: undefined,
                  },
                  replace: true as const,
                }
            : tail === '/chat'
              ? {
                  to: '/admin/users/$userId/chat' as const,
                  params: { userId },
                  search: { hid: String(data.households[0].household_id) },
                  replace: true as const,
                }
              : tail === '/journal'
                ? {
                    to: '/admin/users/$userId/journal' as const,
                    params: { userId },
                    search: { hid: String(data.households[0].household_id) },
                    replace: true as const,
                  }
                : tail === '/audit'
                  ? {
                      to: '/admin/users/$userId/audit' as const,
                      params: { userId },
                      search: { hid: String(data.households[0].household_id) },
                      replace: true as const,
                    }
                  : tail === '/actions'
                    ? {
                        to: '/admin/users/$userId/actions' as const,
                        params: { userId },
                        search: { hid: String(data.households[0].household_id) },
                        replace: true as const,
                      }
                    : tail === '/tools'
                      ? {
                          to: '/admin/users/$userId/tools' as const,
                          params: { userId },
                          search: { hid: String(data.households[0].household_id) },
                          replace: true as const,
                        }
                      : tail === '/exports'
                      ? {
                          to: '/admin/users/$userId/exports' as const,
                          params: { userId },
                          search: { hid: String(data.households[0].household_id) },
                          replace: true as const,
                        }
                      : {
                          to: '/admin/users/$userId' as const,
                          params: { userId },
                          search: { hid: String(data.households[0].household_id) },
                          replace: true as const,
                        };

    const timer = window.setTimeout(() => {
      if (!window.location.pathname.startsWith(userBase)) return;
      navigate(payload);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [data, hid, householdIds, navigate, pathTail, userId]);

  const tabClass = (suffix: string) => {
    const active =
      suffix === ''
        ? pathname === base || pathname === `${base}/`
        : pathname.startsWith(`${base}${suffix}`);
    return active
      ? 'border-nova-primary text-nova-primary font-semibold'
      : 'border-transparent text-nova-muted-foreground hover:text-nova-foreground';
  };

  const displayName = formatDisplayName(data?.first_name, data?.last_name, `User #${id}`);

  const hasHouseholds = (data?.households.length ?? 0) > 0;
  const defaultHid =
    hasHouseholds && data ? String(data.households[0].household_id) : undefined;

  const tabSearch = (): { hid: string | undefined } => ({
    hid: defaultHid ? (hid ?? defaultHid) : undefined,
  });

  const handleBack = () => {
    if (canGoBack) {
      window.history.back();
      return;
    }
    void navigate({ to: '/admin' });
  };

  return (
    <div className='space-y-4'>
      <button
        type='button'
        onClick={handleBack}
        className='inline-flex items-center gap-2 text-sm font-semibold text-nova-muted-foreground hover:text-nova-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-nova-ring focus-visible:ring-offset-2 rounded-sm'
      >
        <FiArrowLeft className='w-4 h-4' />
        Back
      </button>

      {userQuery.isLoading ? (
        <div className='flex justify-center py-12'>
          <div className='animate-spin h-8 w-8 border-2 border-nova-primary border-t-transparent rounded-full' />
        </div>
      ) : userQuery.isError ? (
        <p className='text-red-600'>Failed to load user.</p>
      ) : !data ? null : (
        <>
          {nameBanner ? (
            <div
              className={`rounded-lg border px-4 py-3 text-sm ${
                nameBanner.ok
                  ? 'bg-green-50 border-green-200 text-green-900'
                  : 'bg-red-50 border-red-200 text-red-800'
              }`}
            >
              {nameBanner.text}
            </div>
          ) : null}

          <div className='bg-nova-card rounded-lg shadow-sm border border-nova-border p-4 flex flex-wrap items-center justify-between gap-3'>
            <div>
              <div className='flex flex-wrap items-center gap-2'>
                <h1 className='text-xl font-semibold text-nova-foreground'>{displayName}</h1>
                <button
                  type='button'
                  onClick={() => {
                    setEditNameError(null);
                    setEditNameOpen(true);
                    track('Admin User Name Edit Clicked', { user_id: data.id });
                  }}
                  className='px-2.5 py-1 text-xs font-semibold rounded-md border border-nova-input text-nova-foreground hover:bg-nova-accent'
                >
                  Edit name
                </button>
              </div>
              <p className='text-sm text-nova-muted-foreground mt-1'>
                User ID {data.id}
                {data.deleted_at ? (
                  <span className='ml-2 inline-flex px-2 py-0.5 rounded-md bg-red-100 text-red-700 text-xs font-semibold'>
                    Deleted
                  </span>
                ) : null}
              </p>
            </div>
            <div className='flex flex-wrap gap-2'>
              {data.subscribed ? (
                <span className='inline-flex px-2 py-0.5 rounded-md bg-green-100 text-green-700 text-xs font-semibold'>
                  Subscribed{data.subscription_override ? ' (override)' : ''}
                </span>
              ) : (
                <span className='text-nova-muted-foreground text-xs'>Not subscribed</span>
              )}
              {data.has_push_token ? (
                <span className='inline-flex px-2 py-0.5 rounded-md bg-blue-100 text-blue-800 text-xs font-semibold'>
                  Push token
                </span>
              ) : (
                <span className='inline-flex px-2 py-0.5 rounded-md bg-nova-muted text-nova-muted-foreground text-xs'>
                  No push token
                </span>
              )}
            </div>
          </div>

          <div className='bg-nova-card rounded-lg shadow-sm border border-nova-border p-4 text-sm text-nova-foreground space-y-2'>
            <div className='flex flex-wrap gap-x-6 gap-y-1'>
              <span>
                <span className='text-nova-muted-foreground'>Email:</span> {data.email ?? '—'}
              </span>
              <span>
                <span className='text-nova-muted-foreground'>Phone:</span> {data.phone_number ?? '—'}
              </span>
              <span>
                <span className='text-nova-muted-foreground'>Clerk:</span>{' '}
                <code className='text-xs bg-nova-muted px-1 rounded'>{data.clerk_id ?? '—'}</code>
              </span>
            </div>
            {hasHouseholds && data.households.length > 1 ? (
              <div className='pt-2 border-t border-nova-border'>
                <span className='text-xs font-semibold text-nova-muted-foreground mr-2'>Household:</span>
                <div className='inline-flex flex-wrap gap-2 mt-1'>
                  {data.households.map((h) => (
                    <Link
                      key={h.household_id}
                      to='.'
                      search={{ hid: String(h.household_id) }}
                      className={`px-2 py-1 rounded-md text-xs font-medium border ${
                        hid === String(h.household_id)
                          ? 'border-nova-primary bg-nova-primary/5 text-nova-primary'
                          : 'border-nova-border text-nova-foreground hover:bg-nova-accent'
                      }`}
                    >
                      #{h.household_id}
                    </Link>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <nav className='flex flex-wrap gap-2 border-b border-nova-border pb-px'>
            <Link
              to='/admin/users/$userId'
              params={{ userId }}
              search={tabSearch()}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('')}`}
            >
              Overview
            </Link>
            {hasHouseholds ? (
              <Link
                to='/admin/users/$userId/households'
                params={{ userId }}
                search={tabSearch()}
                className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/households')}`}
              >
                Households
              </Link>
            ) : null}
            {hasHouseholds ? (
              <Link
                to='/admin/users/$userId/expenses'
                params={{ userId }}
                search={tabSearch()}
                className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/expenses')}`}
              >
                Expenses
              </Link>
            ) : null}
            {hasHouseholds ? (
              <Link
                to='/admin/users/$userId/graphs'
                params={{ userId }}
                search={{ ...tabSearch(), period: undefined }}
                className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/graphs')}`}
              >
                Graphs
              </Link>
            ) : null}
            {hasHouseholds ? (
              <Link
                to='/admin/users/$userId/chat'
                params={{ userId }}
                search={tabSearch()}
                className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/chat')}`}
              >
                Chat
              </Link>
            ) : null}
            {hasHouseholds ? (
              <Link
                to='/admin/users/$userId/journal'
                params={{ userId }}
                search={tabSearch()}
                className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/journal')}`}
              >
                Journal
              </Link>
            ) : null}
            <Link
              to='/admin/users/$userId/audit'
              params={{ userId }}
              search={tabSearch()}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/audit')}`}
            >
              Audit
            </Link>
            <Link
              to='/admin/users/$userId/actions'
              params={{ userId }}
              search={tabSearch()}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/actions')}`}
            >
              Actions
            </Link>
            <Link
              to='/admin/users/$userId/tools'
              params={{ userId }}
              search={tabSearch()}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/tools')}`}
            >
              Tools
            </Link>
            <Link
              to='/admin/users/$userId/exports'
              params={{ userId }}
              search={tabSearch()}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/exports')}`}
            >
              Exports
            </Link>
          </nav>

          <Outlet />

          <EditNameDialog
            open={editNameOpen}
            firstName={data.first_name}
            lastName={data.last_name}
            pending={nameMutation.isPending}
            error={editNameError}
            onClose={() => {
              if (!nameMutation.isPending) setEditNameOpen(false);
            }}
            onConfirm={(firstName, lastName) => {
              track('Admin User Name Edit Confirmed', { user_id: data.id });
              nameMutation.mutate({ first_name: firstName, last_name: lastName });
            }}
          />
        </>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/users/$userId')({
  validateSearch: (search: Record<string, unknown>) => ({
    hid: typeof search.hid === 'string' ? search.hid : undefined,
  }),
  component: AdminUserDetailLayout,
});
